import { useState } from "react";
import {
  Mic,
  MessageSquare,
  FolderOpen,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Pin,
  Radio,
  ShieldCheck,
} from "lucide-react";

// Amanda persona — voice/SMS intake bridge. Backend (Twilio/GVO + Plaud) not
// yet wired. This card is the UX hook so leaders understand what the Cloud
// Folders column is FOR when the backend lights up.
const AMANDA_NUMBER_PLACEHOLDER = "[GVO/Twilio Number]";

export default function AmandaIntake() {
  const [open, setOpen] = useState(false);
  // Purely a UX preview toggle — no backend wiring yet.
  const [plaudEnabled, setPlaudEnabled] = useState(true);

  return (
    <section
      className="rounded-sm border relative overflow-hidden mb-6"
      style={{
        borderColor: "rgba(167,139,250,0.35)",
        background:
          "linear-gradient(180deg, rgba(167,139,250,0.09) 0%, rgba(6,11,20,0.7) 100%)",
      }}
      data-testid="amanda-intake"
    >
      <div
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(167,139,250,0.75), transparent)",
        }}
      />

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-4 flex items-center gap-4 text-left"
        data-testid="amanda-intake-toggle"
      >
        <div
          className="w-10 h-10 rounded-sm border flex items-center justify-center shrink-0"
          style={{
            borderColor: "rgba(167,139,250,0.55)",
            background: "rgba(167,139,250,0.16)",
          }}
        >
          <Mic size={18} className="text-[#A78BFA]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.34em] text-[#A78BFA] mb-0.5 flex items-center gap-2">
            <Sparkles size={10} /> Meet Amanda · Last 50 Mile Grid Intake
          </div>
          <div className="font-display text-base sm:text-lg text-white leading-tight">
            Zero-Touch Field Monitoring — your AI team on the Last 50 Mile.
          </div>
        </div>
        <span className="text-slate-400 shrink-0">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>

      {open && (
        <div
          className="px-5 pb-5 pt-1 space-y-5 border-t border-white/5"
          data-testid="amanda-intake-body"
        >
          <p className="text-sm text-slate-300 leading-relaxed">
            Amanda is the intake node on the{" "}
            <span className="text-gold font-display">Last 50 Mile Grid</span>. Field workers
            can voice-record or SMS notes to{" "}
            <span
              className="font-mono-aviation px-1.5 py-0.5 rounded-sm text-[#A78BFA]"
              style={{
                background: "rgba(167,139,250,0.12)",
                border: "1px solid rgba(167,139,250,0.4)",
              }}
              data-testid="amanda-intake-number"
            >
              {AMANDA_NUMBER_PLACEHOLDER}
            </span>
            , and your{" "}
            <span className="text-white font-display">AI utility team</span> transcribes and
            files them into the Cloud folder for that lead — no laptop required.
          </p>

          {/* Plaud NotePin block */}
          <div
            className="rounded-sm border p-4"
            style={{
              borderColor: "rgba(212,175,55,0.4)",
              background:
                "linear-gradient(180deg, rgba(212,175,55,0.06) 0%, rgba(6,11,20,0.5) 100%)",
            }}
            data-testid="plaud-notepin-block"
          >
            <div className="flex items-start gap-3">
              <div
                className="w-9 h-9 rounded-sm border flex items-center justify-center shrink-0"
                style={{
                  borderColor: "rgba(212,175,55,0.55)",
                  background: "rgba(212,175,55,0.16)",
                }}
              >
                <Pin size={16} className="text-gold" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.32em] text-gold mb-0.5 flex items-center gap-1.5">
                  <Radio size={10} /> Plaud NotePin Support
                </div>
                <div className="font-display text-white text-base leading-tight">
                  Passive Audio Sync
                </div>
                <div className="text-xs text-slate-300 mt-1.5 leading-relaxed">
                  Clip a Plaud NotePin to the rep. Every job-site conversation
                  is captured, streamed to Amanda, transcribed, tagged by lead,
                  and archived in Drive — hands-free, phone-free.
                </div>

                {/* Toggle */}
                <div className="mt-4 flex items-center justify-between gap-3">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-slate-400 flex items-center gap-1.5">
                    <ShieldCheck size={12} className="text-emerald-400" />
                    Enable Passive Audio Sync
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={plaudEnabled}
                    onClick={() => setPlaudEnabled((v) => !v)}
                    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors border"
                    style={{
                      backgroundColor: plaudEnabled
                        ? "rgba(212,175,55,0.35)"
                        : "rgba(148,163,184,0.15)",
                      borderColor: plaudEnabled
                        ? "rgba(212,175,55,0.7)"
                        : "rgba(148,163,184,0.35)",
                    }}
                    data-testid="plaud-notepin-toggle"
                  >
                    <span
                      className="inline-block h-4 w-4 transform rounded-full transition-transform"
                      style={{
                        backgroundColor: plaudEnabled ? "#F5C842" : "#94A3B8",
                        transform: plaudEnabled
                          ? "translateX(22px)"
                          : "translateX(4px)",
                        boxShadow: plaudEnabled
                          ? "0 0 10px rgba(245,200,66,0.7)"
                          : "none",
                      }}
                    />
                  </button>
                </div>

                <div
                  className="mt-2 text-[10px] uppercase tracking-[0.22em] font-mono-aviation"
                  style={{ color: plaudEnabled ? "#F5C842" : "#64748B" }}
                  data-testid="plaud-notepin-status"
                >
                  {plaudEnabled
                    ? "· Passive Sync Armed · Awaiting hardware pair"
                    : "· Passive Sync Off"}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Step
              n={1}
              icon={<Mic size={14} />}
              title="Record or Text"
              body="Rep sends a voice note, SMS, or wears the Plaud NotePin."
            />
            <Step
              n={2}
              icon={<MessageSquare size={14} />}
              title="Amanda Transcribes"
              body="Auto-transcribed, tagged, and routed by lead name."
            />
            <Step
              n={3}
              icon={<FolderOpen size={14} />}
              title="Filed in Drive"
              body="Lands inside the lead's Cloud sub-folder in seconds."
            />
          </div>

          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-mono-aviation flex items-center gap-2">
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: "#A78BFA" }}
            />
            Backend intake goes live once the {AMANDA_NUMBER_PLACEHOLDER} line is provisioned.
          </div>
        </div>
      )}
    </section>
  );
}

function Step({ n, icon, title, body }) {
  return (
    <div
      className="rounded-sm border p-3"
      style={{
        borderColor: "rgba(255,255,255,0.08)",
        background: "rgba(6,11,20,0.6)",
      }}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span
          className="w-5 h-5 rounded-sm flex items-center justify-center text-[10px] font-display shrink-0"
          style={{
            background: "rgba(167,139,250,0.14)",
            color: "#A78BFA",
            border: "1px solid rgba(167,139,250,0.4)",
          }}
        >
          {n}
        </span>
        <span className="text-[#A78BFA]">{icon}</span>
        <span className="font-display text-sm text-white">{title}</span>
      </div>
      <div className="text-[11px] text-slate-400 leading-relaxed">{body}</div>
    </div>
  );
}
